import { useEffect, useRef } from "react";
import type { InlineFieldProps } from "./types";
import { INLINE_INPUT_CLASS } from "./inline-field-styles";

const NUMERIC_TYPES = new Set(["Int", "Float", "Currency", "Percent"]);

/**
 * Parse raw input string into the correct type for the field.
 * Frappe numeric fields default to 0 server-side, so we use 0 as fallback.
 */
function parseFieldValue(fieldtype: string, raw: string): unknown {
  const trimmed = raw.trim();
  if (fieldtype === "Int") {
    const n = parseInt(trimmed, 10);
    return isNaN(n) ? 0 : n;
  }
  if (NUMERIC_TYPES.has(fieldtype)) {
    const n = parseFloat(trimmed);
    return isNaN(n) ? 0 : n;
  }
  return trimmed;
}

/**
 * Format the stored value for editing (strip currency formatting, etc.).
 * Edit mode shows raw numbers, not formatted display strings.
 */
function formatEditValue(fieldtype: string, value: unknown): string {
  if (value === null || value === undefined) return "";
  if (NUMERIC_TYPES.has(fieldtype)) {
    const n = typeof value === "string" ? parseFloat(value) : Number(value);
    if (isNaN(n) || n === 0) return "";
    return String(n);
  }
  return String(value);
}

/**
 * Inline text input for Data, Int, Float, Currency, Percent fields.
 *
 * - Auto-focuses and selects all text on mount
 * - Commits on blur or Enter
 * - Cancels on Escape
 * - Uses uncontrolled input (defaultValue) to avoid cursor jump
 * - Double-commit guard via committingRef
 */
export function InlineFieldInput({
  field,
  value,
  onCommit,
  onCancel,
  placeholder,
  onAdvance,
}: InlineFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const committingRef = useRef(false);

  useEffect(() => {
    const el = inputRef.current;
    if (el) {
      el.focus();
      el.select();
    }
  }, []);

  const handleCommit = () => {
    if (committingRef.current) return;
    committingRef.current = true;
    const raw = inputRef.current?.value ?? "";
    const parsed = parseFieldValue(field.fieldtype, raw);
    // Compare with original — skip save if unchanged
    if (parsed !== value && !(parsed === 0 && (value === 0 || value === null || value === undefined || value === ""))) {
      onCommit(parsed);
    } else if (parsed === 0 && NUMERIC_TYPES.has(field.fieldtype) && (value === null || value === undefined || value === "")) {
      // User explicitly cleared a numeric field → save as 0
      onCommit(0);
    } else {
      onCancel();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      onCancel();
    }
    if (e.key === "Enter") {
      e.preventDefault();
      handleCommit();
    }
    if (e.key === "Tab" && onAdvance) {
      e.preventDefault(); // prevent default tab — we control focus
      handleCommit();
      onAdvance(e.shiftKey ? "prev" : "next");
    }
    // Without onAdvance: Tab → blur fires → handleCommit (default behavior)
  };

  return (
    <input
      ref={inputRef}
      defaultValue={formatEditValue(field.fieldtype, value)}
      onBlur={handleCommit}
      onKeyDown={handleKeyDown}
      className={INLINE_INPUT_CLASS}
      placeholder={placeholder}
      inputMode={NUMERIC_TYPES.has(field.fieldtype) ? "decimal" : "text"}
    />
  );
}
